import { CircleDollarSign, Edit, Eye, Layers, TriangleAlert } from 'lucide-react';
import type { Plan } from '../types/plans.types';
import {
  formatPlanPrice,
  getSyncStatusLabel,
  getSyncStatusVariant,
  getVisibilityLabel,
} from '../utils/plan-sync-status';
import { useAuthorization } from '@/core/hooks/useAuthorization';
import Protected from '@/shared/components/ahkinia/Protected/Protected';
import { Switch } from '@/shared/components/ahkinia/Switch/switch';
import { Button } from '@/shared/components/ui/button';
import type { DataTableColumn } from '@/shared/components/ui/data-table';
import { DataTable } from '@/shared/components/ui/data-table';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { PERMISSIONS } from '@/shared/constants/permissions';

interface PlansTableProps {
  data: Plan[];
  onViewDetail?: (planId: string) => void;
  onEdit?: (planId: string) => void;
  onEditPricing?: (planId: string) => void;
  onToggle?: (planId: string) => void;
}

export function PlansTable({
  data,
  onViewDetail,
  onEdit,
  onEditPricing,
  onToggle,
}: PlansTableProps) {
  const { hasPermission } = useAuthorization();
  const canToggle = hasPermission(PERMISSIONS.PLANS_MANAGE);

  const columns: DataTableColumn<Plan>[] = [
    {
      key: 'name',
      header: 'Plan',
      render: (row) => (
        <div className="flex flex-col">
          <span data-testid={`plan-name-${row.id}`} className="text-light text-sm">
            {row.name}
          </span>
          {row.description && (
            <span className="text-primary text-xs line-clamp-1">{row.description}</span>
          )}
        </div>
      ),
    },
    {
      key: 'visibility',
      header: 'Visibilidad',
      render: (row) => (
        <span data-testid={`plan-visibility-${row.id}`} className="text-light text-sm">
          {getVisibilityLabel(row.visibility)}
        </span>
      ),
    },
    {
      key: 'priceMonthly',
      header: 'Mensual',
      render: (row) => (
        <span data-testid={`plan-price-monthly-${row.id}`} className="text-light text-sm">
          {formatPlanPrice(row.priceMonthly)}
        </span>
      ),
    },
    {
      key: 'priceAnnual',
      header: 'Anual',
      render: (row) => (
        <span data-testid={`plan-price-annual-${row.id}`} className="text-light text-sm">
          {formatPlanPrice(row.priceAnnual)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      render: (row) => (
        <div data-testid={`plan-status-${row.id}`}>
          <Switch
            checked={row.isActive}
            disabled={!canToggle}
            onCheckedChange={() => onToggle?.(row.id)}
          />
        </div>
      ),
    },
    {
      key: 'syncStatus',
      header: 'Sincronización',
      render: (row) => (
        <div className="flex items-center gap-2">
          <div data-testid={`plan-sync-status-${row.id}`}>
            <StatusBadge
              status={getSyncStatusVariant(row.syncStatus)}
              label={getSyncStatusLabel(row.syncStatus)}
            />
          </div>
          {row.isActive && !row.isOfferable && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span data-testid={`plan-not-offerable-${row.id}`}>
                  <TriangleAlert size={16} className="text-warn" />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Activo pero no se está ofreciendo: le faltan precios en Stripe.
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <div className="flex items-center gap-1">
          <Button
            data-testid={`view-plan-${row.id}`}
            iconImg={<Eye />}
            title="Ver detalle"
            onClick={() => onViewDetail?.(row.id)}
          />
          <Protected permissions={PERMISSIONS.PLANS_MANAGE}>
            <Button
              data-testid={`edit-plan-${row.id}`}
              iconImg={<Edit />}
              title="Editar"
              onClick={() => onEdit?.(row.id)}
            />
          </Protected>
          <Protected permissions={PERMISSIONS.PLANS_MANAGE}>
            <Button
              data-testid={`edit-pricing-${row.id}`}
              iconImg={<CircleDollarSign />}
              title="Cambiar precio"
              onClick={() => onEditPricing?.(row.id)}
            />
          </Protected>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      title="Planes"
      titleIcon={<Layers />}
      subtitle="Catálogo completo, incluidos los privados y los retirados"
      rows={data}
      columns={columns}
      getRowKey={(row) => row.id}
      testIdPrefix="plans"
      emptyMessage="No hay planes configurados."
    />
  );
}
